/**
 * The ground painted off the main thread. Painting a chunk of ground pixel by pixel takes a good
 * fraction of a second; a couple of workers paint them (groundWorker.ts)
 * and hand back finished pictures, the ones in view first, then the ring just outside it.
 */
import { truePxScene } from './truepx/flags';
import type { GameMap, IsoOrigin } from '@nine/shared';

export interface GroundRemote {
  /** Asks for chunk `key` (ground pixels X0, Y0, CW×CH); `got` receives its picture. */
  ask(
    key: number,
    X0: number,
    Y0: number,
    CW: number,
    CH: number,
    urgent: boolean,
    /** Called with a quick picture at 1, then the full one (`last`). */
    got: (img: ImageBitmap, last: boolean) => void,
  ): void;
  /** The chunk is no longer kept: drop it if it is still waiting. */
  forget(key: number): void;
  /** This ground is done with (the camera turned, the zone changed). */
  close(): void;
  /** False once the workers have failed: the ground then paints itself. */
  readonly alive: boolean;
}

interface Req {
  gid: number;
  key: number;
  X0: number;
  Y0: number;
  CW: number;
  CH: number;
  got: (img: ImageBitmap, last: boolean) => void;
}

let pool: { w: Worker; busy: boolean }[] | null | undefined;
const urgent: Req[] = [];
const ahead: Req[] = [];
const out = new Map<string, Req>();
const closed = new Set<number>();
let nextGid = 1;
let alive = true;

/** Ground chunks asked for so far, and how many are still being painted (for the loading screen). */
export const groundJobs = { asked: 0, waiting: (): number => urgent.length + ahead.length + out.size };

function start(): typeof pool {
  if (pool !== undefined) return pool;
  if (
    typeof document === 'undefined' ||
    typeof Worker === 'undefined' ||
    typeof OffscreenCanvas === 'undefined'
  )
    return (pool = null);
  try {
    pool = Array.from({ length: 2 }, () => {
      const w = new Worker(new URL('./groundWorker.ts', import.meta.url), { type: 'module' });
      const slot = { w, busy: false };
      w.onmessage = (e: MessageEvent<{ gid: number; key: number; bitmap: ImageBitmap; last: boolean }>) => {
        const { gid, key, bitmap, last } = e.data;
        const req = out.get(`${gid}:${key}`);
        if (req && !closed.has(gid)) req.got(bitmap, last);
        else bitmap.close();
        if (!last) return;
        slot.busy = false;
        out.delete(`${gid}:${key}`);
        feed();
      };
      w.onerror = () => {
        alive = false;
        for (const p of pool ?? []) p.w.terminate();
        pool = null;
      };
      return slot;
    });
  } catch {
    pool = null;
  }
  return pool;
}

function feed(): void {
  if (!pool) return;
  for (const slot of pool) {
    if (slot.busy) continue;
    const req = urgent.shift() ?? ahead.shift();
    if (!req) return;
    slot.busy = true;
    out.set(`${req.gid}:${req.key}`, req);
    slot.w.postMessage({
      type: 'chunk',
      gid: req.gid,
      key: req.key,
      X0: req.X0,
      Y0: req.Y0,
      CW: req.CW,
      CH: req.CH,
    });
  }
}

const drop = (keep: (r: Req) => boolean) => {
  for (const q of [urgent, ahead]) {
    const kept = q.filter(keep);
    q.length = 0;
    q.push(...kept);
  }
};

/**
 * Hands one map's ground (as seen at the camera's quarter turn) to the workers, painting at `res`
 * canvas pixels per ground pixel. Null where workers cannot paint: the ground paints itself.
 */
export function openGround(
  m: GameMap,
  o: IsoOrigin,
  at: { n: number; rot: number },
  res: number,
): GroundRemote | null {
  const workers = start();
  if (!workers) return null;
  const gid = nextGid++;
  try {
    for (const { w } of workers) w.postMessage({ type: 'open', gid, m, o, n: at.n, rot: at.rot, res, truePx: truePxScene() });
  } catch {
    // The shared map now holds functions (lazy chunks), which cannot be cloned to a worker: paint on the main thread.
    return null;
  }
  return {
    ask(key, X0, Y0, CW, CH, isUrgent, got) {
      const req = { gid, key, X0, Y0, CW, CH, got };
      groundJobs.asked++;
      // A chunk that has come into view jumps from the look-ahead queue to the front.
      if (isUrgent) {
        drop((r) => !(r.gid === gid && r.key === key));
        urgent.push(req);
      } else ahead.push(req);
      feed();
    },
    forget(key) {
      drop((r) => !(r.gid === gid && r.key === key));
    },
    close() {
      closed.add(gid);
      drop((r) => r.gid !== gid);
      for (const { w } of pool ?? []) w.postMessage({ type: 'close', gid });
    },
    get alive() {
      return alive;
    },
  };
}
